import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const Ctx = createContext(null);

function norm(opts) {
  return typeof opts === "string" ? { title: opts } : opts || {};
}

export function ConfirmProvider({ children }) {
  const [req, setReq] = useState(null);
  const confirm = useCallback((opts) => new Promise((resolve) => setReq({ mode: "confirm", opts: norm(opts), resolve })), []);
  const prompt = useCallback((opts) => new Promise((resolve) => setReq({ mode: "prompt", opts: norm(opts), resolve })), []);
  const close = (value) => { req?.resolve(value); setReq(null); };
  return (
    <Ctx.Provider value={{ confirm, prompt }}>
      {children}
      {req && <ConfirmDialog mode={req.mode} opts={req.opts} onClose={close} />}
    </Ctx.Provider>
  );
}

export function useConfirm() {
  return useContext(Ctx);
}

function ConfirmDialog({ mode, opts, onClose }) {
  const [value, setValue] = useState(opts.defaultValue ?? "");
  const inputRef = useRef(null);
  const cancelValue = mode === "prompt" ? null : false;
  const okValue = mode === "prompt" ? value : true;
  const okDisabled = mode === "prompt" && opts.required && !value.trim();

  useEffect(() => {
    inputRef.current?.focus();
    const onKey = (e) => { if (e.key === "Escape") onClose(cancelValue); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const okCls = opts.tone === "neutral" ? "bg-black hover:bg-zinc-800" : "bg-[#c8102e] hover:bg-[#a50c24]";
  return createPortal(
    <div className="fixed inset-0 z-[90] bg-black/60 flex items-end sm:items-center justify-center p-4 pb-24 sm:pb-4" data-testid="confirm-dialog" role="dialog" aria-modal="true">
      <form className="bg-white rounded-2xl w-full max-w-sm p-4" onSubmit={(e) => { e.preventDefault(); if (!okDisabled) onClose(okValue); }}>
        <div className="font-display font-bold text-base" data-testid="confirm-dialog-title">{opts.title}</div>
        {opts.description && <p className="text-xs text-zinc-600 mt-1 whitespace-pre-wrap" data-testid="confirm-dialog-description">{opts.description}</p>}
        {mode === "prompt" && (
          <input ref={inputRef} value={value} onChange={(e) => setValue(e.target.value)} placeholder={opts.placeholder || ""} maxLength={opts.maxLength || 300}
                 inputMode={opts.inputMode} data-testid="confirm-dialog-input" autoComplete="off"
                 className="mt-3 w-full border border-zinc-300 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-black" />
        )}
        <div className="flex gap-2 mt-4">
          <button type="button" onClick={() => onClose(cancelValue)} data-testid="confirm-dialog-cancel" className="flex-1 min-h-[44px] rounded-xl border border-zinc-300 text-sm font-semibold">{opts.cancelLabel || "Cancelar"}</button>
          <button type="submit" disabled={okDisabled} data-testid="confirm-dialog-ok" className={`flex-1 min-h-[44px] rounded-xl text-white text-sm font-semibold disabled:opacity-40 ${okCls}`}>{opts.confirmLabel || "Confirmar"}</button>
        </div>
      </form>
    </div>,
    document.body
  );
}
